import './TryoutDataExportPage.css';

import React, { useCallback, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Button from '../components/common/Button';
import { ArchiveBoxArrowDownIcon } from './TryoutIcons';
import {
  generateTryoutExport,
  TryoutExportError,
} from '../services/exportService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ExportState =
  | { status: 'idle' }
  | { status: 'generating' }
  | { status: 'ready'; downloadUrl: string; exportId: string }
  | { status: 'error'; message: string };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TryoutDataExportPage: React.FC = () => {
  const { tryoutId } = useParams<{ tryoutId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const tryoutName =
    (location.state as { tryoutName?: string } | null)?.tryoutName?.trim() || 'Tryout';

  const [exportState, setExportState] = useState<ExportState>({ status: 'idle' });

  const handleGenerate = useCallback(async (): Promise<void> => {
    if (!tryoutId) return;
    setExportState({ status: 'generating' });
    try {
      const result = await generateTryoutExport(tryoutId);
      setExportState({
        status: 'ready',
        downloadUrl: result.downloadUrl,
        exportId: result.exportId,
      });
    } catch (err) {
      const message =
        err instanceof TryoutExportError
          ? err.message
          : 'An unexpected error occurred. Please try again.';
      setExportState({ status: 'error', message });
    }
  }, [tryoutId]);

  const handleBack = useCallback((): void => {
    navigate('/dashboard');
  }, [navigate]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderBody = (): React.ReactNode => {
    if (exportState.status === 'idle') {
      return (
        <div className="tryout-export-card">
          <ArchiveBoxArrowDownIcon size={40} className="tryout-export-icon" />
          <h2 className="tryout-export-card-title">Export Tryout Data</h2>
          <p className="tryout-export-card-desc">
            Generates a <code>.zip</code> containing a self-contained interactive
            viewer with all athlete scores and comments.
          </p>
          <Button onClick={() => void handleGenerate()}>Generate Export ZIP</Button>
        </div>
      );
    }

    if (exportState.status === 'generating') {
      return (
        <div className="tryout-export-card">
          <div className="tryout-export-spinner" aria-label="Generating export" />
          <p className="tryout-export-card-desc">Generating export&hellip;</p>
        </div>
      );
    }

    if (exportState.status === 'ready') {
      return (
        <div className="tryout-export-card">
          <ArchiveBoxArrowDownIcon size={40} className="tryout-export-icon tryout-export-icon--success" />
          <h2 className="tryout-export-card-title">Export Ready</h2>
          <p className="tryout-export-card-desc">
            Your export is ready. The download link is valid for 7 days.
          </p>
          <a
            href={exportState.downloadUrl}
            download
            className="tryout-export-download-link"
          >
            Download ZIP
          </a>
          <Button
            variant="secondary"
            compact
            onClick={() => setExportState({ status: 'idle' })}
          >
            Regenerate
          </Button>
        </div>
      );
    }

    // error
    return (
      <div className="tryout-export-card">
        <div className="tryout-export-error-callout">{exportState.message}</div>
        <Button
          variant="secondary"
          onClick={() => setExportState({ status: 'idle' })}
        >
          Try Again
        </Button>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="tryout-export-shell">
      <header className="tryout-export-header">
        <button
          type="button"
          className="tryout-export-back-btn"
          onClick={handleBack}
          aria-label="Back to dashboard"
        >
          &#8592; Dashboard
        </button>
        <div className="tryout-export-header-meta">
          <span className="tryout-export-debug-badge">debug</span>
          <span className="tryout-export-title">{tryoutName}</span>
        </div>
      </header>
      <main className="tryout-export-main">{renderBody()}</main>
    </div>
  );
};

export default TryoutDataExportPage;
